import { Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { BookMarkOption, InspectionImage } from '../../../_objects/inspection-image';
import { Subject, tap } from 'rxjs';
import { SpinnerService } from '../../../_services/spinner.service';
import { TranslateService } from '@ngx-translate/core';
import { LangUpdateService } from '../../../_services/lang-update.service';
import { VideoBookmarksStateService } from '../video-bookmark.state.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-video-data-edit',
    templateUrl: './video-data-edit.component.html',
    styleUrls: ['./video-data-edit.component.scss'],
    standalone: false
})
export class VideoDataEditComponent implements OnInit, OnDestroy, OnChanges {

  readonly commentMaxLength = 1000;

  @Input() inspectionImage: InspectionImage;
  @Input() index: number;
  @Input() listCount: number;
  @Input() isPreviewReport: boolean;

  @Output() applyMove = new EventEmitter<boolean>();
  @Output() bookMarksHasChanged = new EventEmitter<boolean>();

  bookmarkOptions: BookMarkOption[];
  imageListIndex: number;
  isBackImage = false;
  isForwardImage = false;
  onDestroy$ = new Subject();
  videoElementEdit: HTMLVideoElement;
  bookmarkTimes: number[] = [];
  isPlayableVideo: boolean = false;
  isLoading: boolean;
  duration = -1;
  isBookMarkSelected: boolean;
  selectedTime: string | null = null;

  get isWideScreen() {
    return window.innerHeight < window.innerWidth;
  }

  constructor(
    private oscSpinnerService: SpinnerService,
    private translate: TranslateService,
    private langService: LangUpdateService,
    private videoBookmarksStateService: VideoBookmarksStateService
  ) {
    this.langService.prepare(translate, this.onDestroy$);
    this.videoBookmarksStateService.bookmarkAdded
      .pipe(takeUntilDestroyed())
      .subscribe((newBookmark) => {
        this.addBookmark(Number(newBookmark.mtime));

      });

    this.videoBookmarksStateService.inspectionBookmarks
      .pipe(takeUntilDestroyed())
      .subscribe((newBookmarks) => {
        this.bookmarkOptions = newBookmarks;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes.inspectionImage)) {
      this.setArrowButton();
      this.setMasters(this.bookmarkOptions);
      this.oscSpinnerService.hide(true);
    }
  }

  ngOnInit(): void {
    this.isLoading = true;
    this.videoElementEdit = document.getElementById("video-playback") as HTMLVideoElement;

    if (!this.videoElementEdit) {
      console.error("Video element not found.");
      return;
    }

    setTimeout(() => {
      this.isLoading = false;
    }, 3000);

    this.loadedDataHandler = this.loadedDataHandler.bind(this);
    this.videoElementEdit.addEventListener('loadeddata', this.loadedDataHandler);
  }

  ngOnDestroy() {
    this.onDestroy$.next(undefined);
    this.onDestroy$.unsubscribe();
    this.videoElementEdit.removeEventListener('loadeddata', this.loadedDataHandler);
  }

  loadedDataHandler() {
    this.isLoading = true;
    this.duration = this.videoElementEdit.duration;
    this.isPlayableVideo = !isNaN(this.duration) && this.duration > 0 && isFinite(this.duration);
    this.isLoading = false;
  }

  setArrowButton() {
    this.isBackImage = this.index > 0;
    this.isForwardImage = this.index < this.listCount - 1;
    this.imageListIndex = this.index;
  }

  changeIndex(isBack: boolean): void {
    this.applyMove.emit(isBack);
  }

  setMasters(bookmarkOptions: BookMarkOption[]): void {
    this.bookmarkTimes = bookmarkOptions.map((item) => this.parseBookmarkToNumber(item.mtime));
  }

  private parseBookmarkToNumber(mtime) {
    return Number(mtime) / 1000;
  }

  onSelect(time: string, event: MouseEvent): void {
    event.stopPropagation();
    this.selectedTime = time;
    this.videoElementEdit.currentTime = Number(time);
    this.isBookMarkSelected = true;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    this.selectedTime = null;
  }

  removeBookmark(index: number): void {
    this.bookmarkTimes = this.bookmarkTimes.filter((item,i) => i !== index );
    this.bookmarkOptions = this.bookmarkOptions.filter((item, i) => i !== index);
    this.videoBookmarksStateService.updateInspectionBookmarks(this.bookmarkOptions);
    this.bookMarksHasChanged.emit(true);
  }

  addBookmark(time: number): void {
    const roundedTime = Number(time.toFixed(3));
    const newMark = this.toMillisecondsString(String(roundedTime));
    this.bookmarkTimes = [...this.bookmarkTimes, roundedTime];
    this.bookmarkOptions.push({ mtime: newMark });
    this.videoBookmarksStateService.updateInspectionBookmarks(this.bookmarkOptions);
    this.bookMarksHasChanged.emit(true);
  }

  getLettPosition(time: number): number {
    if (this.duration < 0) {
      return -1;
    }

    return time / this.duration * 100;
  }

  getMarkTimeLabel(num: number | undefined | null): string {
    if (typeof num !== 'number' || isNaN(num)) {
      return '0.0 ' + this.translate.instant('videoDataEditComponent.seconds');
    }

    return num.toFixed(1) + ' ' + this.translate.instant('videoDataEditComponent.seconds');
  }

 toMillisecondsString(timeStr: string): string {
    const timeInSeconds = parseFloat(timeStr);
    const timeInMilliseconds = Math.round(timeInSeconds * 1000);
    return String(timeInMilliseconds);
  }
}
